import React, { useCallback, useMemo } from "react";
import Flatpickr, { DateTimePickerProps } from "react-flatpickr";

import cx from "classnames";
import dayjs from "dayjs";

import { coalesce } from "@kazoohr/helpers";

import { THEME_COLORS } from "../../theme/themeTokens";
import { ControlWrapper } from "../ControlWrapper";
import { Icon } from "../Icon";
import inputStyles from "../Input/Input.scss";

import styles from "./DatePicker.scss";

import "./Theme.scss";

/**
 * The Flatpickr instance that implements the calendar UX
 */
export interface FlatpickrController {
  clear(): void;
  close(): void;
  input: HTMLInputElement;
  readonly calendarContainer: HTMLDivElement;
}

/**
 * Props for the DatePicker component
 */
export interface DatePickerProps {
  /** Optionally attaches the calendar to the supplied element instead of <body> */
  attachTo?: HTMLElement;
  /** Position of the calendar. Default: "auto" */
  position?:
    | "auto"
    | "above"
    | "below"
    | "above left"
    | "above right"
    | "below left"
    | "below center"
    | "below right"
    | HTMLDivElement;
  /** Limit the calendar input to the a specified width. Default: "default" */
  inputMaxWidth?: "small" | "default" | "large";
  /** Make the input a compact input size. Default: false */
  compact?: boolean;
  /** Allow keyboard input. Default true */
  allowInput?: boolean;
  /** Disable the input. Default: false */
  disabled?: boolean;
  /** A default date to display. Takes plain old JS Date object, a string, or an array of dates */
  defaultDate?: Date | Date[] | string;
  /** Error message to be displayed. If truthy will also show error styling */
  error?: string | null;
  /** Grows the input to the full width of the container. Default: false */
  fullWidth?: boolean;
  /** The HTML id attribute to be assigned to the input. Defaults to `date-picker-n` */
  id?: string;
  /** Date or string of the first selectable date */
  firstSelectableDate?: Date | string;
  /** Helper text for the control */
  helperText?: string;
  /** Label for the control */
  label?: string;
  /** Date or string of the last selectable date */
  lastSelectableDate?: Date | string;
  /** String value of the calendar mode. Single date, or date range. Default: `single` */
  mode?: "single" | "range" | "multiple";
  /** Function receiving the new date(s) when they are updated. Always sends an array as the first and only argument */
  onChange:
    | ((dates: Date[]) => void)
    | ((dates: Date) => void)
    | ((dates: string[]) => void);
  /** Function receiving date(s) and datepicker instance when calendar component opens */
  onOpen?: (dates: Date[]) => void;
  /** Function receiving date(s) and datepicker instance when calendar component closes */
  onClose?: (dates: Date[]) => void;
  /** Display a button to clear the date. Default: false */
  showClearButton?: boolean;
  /** Whether to show the calendar icon by the input. Default: true */
  showIcon?: boolean;
  /** A reference to the object that implements the calendar UX */
  flatpickrRef?: React.MutableRefObject<FlatpickrController | null>;
  /** A label that marks this date as optional */
  optionalLabelText?: string;
  /** Controls the format the date will be displayed in. See https://flatpickr.js.org/formatting/ */
  dateFormat?: string;
  /** Input tooltip */
  tooltip?: React.ReactNode;
  /** Placeholder text for the input, to be shown when the user has not selected or has cleared the dates */
  placeholder?: string;
  /** It will render as a data-testid prop on the Pill component */
  "data-testid"?: string;
  /** Custom CSS class for the calendar container */
  calendarContainerClass?: string;
  /** The current value of the date picker */
  value?: Date[] | Date | string | string[];
  /** Flatpickr plugins */
  plugins?: any[];
}

/**
 * None of this comment is new behavior. I'm documenting because it is a footgun for engineers.
 *
 * IF YOUR DATE VALUE HAS A TZ ASSOCIATED WITH IT YOU MUST USE THE PACKAGE DATEPICKER WRAPPER COMPONENT OR CHANGE YOUR VALUE TO NOT HAVE A TZ.
 *
 * The `DatePicker` component requires the value or default value to be a date with no tz association due to flatpicker bugs. See parsedValue comments below.
 * As mentioned in those parsedValue comments below, we can't provide a value prop to Flatpicker with no timezone with creating bugs.
 * That means we must create a date object with a TZ. However, if the TZ comes from anywhere but the current browser the user is logged in as,
 * we may convert the date to the wrong day when we create this date object. (IE creating 1 of date bugs)
 *
 * For ex, If the value prop is provided directly to the datepicker component as a string with a timezone,  we will convert the date to whatever timezone
 * the user's browser is in. IE if a date is passed into Datepicker component as 2025-08-04T05:00:00.000Z (CT), calling and a user's browser is in mtn time, toDate results in a value of 2025-08-04 23:00:00 GMT-0600
 * this, shows a different and wrong date to the customer.
 */
// eslint-disable-next-line max-lines-per-function
export default function DatePicker({
  attachTo,
  position,
  compact = false,
  inputMaxWidth = "default",
  defaultDate,
  disabled = false,
  error = null,
  fullWidth = false,
  id,
  helperText = "",
  optionalLabelText = "",
  label,
  mode = "single",
  onChange,
  onOpen,
  onClose,
  showClearButton = false,
  showIcon = true,
  firstSelectableDate,
  lastSelectableDate,
  dateFormat = `F j, Y`,
  allowInput = true,
  tooltip,
  "data-testid": testId,
  flatpickrRef: flatpickrOutputRef,
  placeholder,
  calendarContainerClass,
  plugins = [],
  value,
}: DatePickerProps): JSX.Element {
  const reactUseId = React.useId();
  id = coalesce(id, `date-picker-${reactUseId}`);
  const flatpickrObjectRef = React.useRef<FlatpickrController | null>(null);
  const inputHasValue = Boolean(flatpickrObjectRef?.current?.input?.value);

  /**
   * When the value prop is provided as a string, Flatpickr will assume the timezone as UCT which can lead to wrong dates being displayed to users.
   * This is because flatpicker converts a date to UTC (2025-08-05T00:00:00.000Z), and then whatever timezone the browser is currently in, which changes the date (Mon Aug 04 2025 18:00:00 GMT-0600 (Mountain Daylight Time).
   * This results in the user seeing the wrong day when we don't pass in a date with a tz associate with it. The below function ensures
   * when given a date with no timezone, we create a date with the current user browsers timezone.
   */
  const parsedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === "string") {
      return dayjs(value).toDate();
    }
    return value;
  }, [value]);

  const inputClassNames = cx(
    cx(inputStyles.input, styles.input, {
      [inputStyles["input--compact"]]: compact,
      [inputStyles["input--disabled"]]: disabled,
      [inputStyles["input--error"]]: Boolean(error),
    })
  );

  React.useEffect(() => {
    // istanbul ignore else - noop
    if (flatpickrObjectRef.current) {
      // HACK: Flatpickr doesn't give us a way to put an ID directly on
      // the input it generates. So we need to do this to ensure our forms
      // are more accessible.
      const nextSibling = flatpickrObjectRef.current.input.nextSibling;
      if (nextSibling && "id" in nextSibling && "autocomplete" in nextSibling) {
        Object.assign(nextSibling, { id, autocomplete: "off" });
      }

      if (flatpickrOutputRef) {
        flatpickrOutputRef.current = flatpickrObjectRef.current;
      }
    }
  }, [id, flatpickrOutputRef]);

  React.useEffect(() => {
    // HACK: See above. React doesn't control this, so we need to
    // improvise.
    if (flatpickrObjectRef.current) {
      const nextSibling = flatpickrObjectRef.current.input.nextSibling;
      if (nextSibling && "className" in nextSibling) {
        Object.assign(nextSibling, { className: inputClassNames });
      }
    }
  }, [inputClassNames]);

  // If we have a defaultDate that is before the firstSelectableDate, Flatpickr will show a blank input
  // To avoid this, we need to have a custom date disabled function, that allows selecting the defaultDate and
  // disables any other dates outside the firstSelectableDate to lastSelectableDate range
  const disabledDates = React.useMemo(() => {
    // If we don't have date constraints, then the default behaviour is fine
    if (!firstSelectableDate && !lastSelectableDate) {
      return undefined;
    }
    const formattedFirstSelectableDate = dayjs(firstSelectableDate);
    const formattedLastSelectableDate = dayjs(lastSelectableDate);
    const formattedDefaultDate = dayjs(
      Array.isArray(defaultDate) ? defaultDate[0] : defaultDate
    );
    return (inputDate: Date): boolean => {
      const formattedInput = dayjs(inputDate);

      // Enable the defaultDate
      if (formattedInput.isSame(formattedDefaultDate, "day")) {
        return false;
      }
      let validDate = true;
      // Disable any dates before firstSelectableDate
      if (
        firstSelectableDate &&
        formattedInput.isBefore(formattedFirstSelectableDate, "day")
      ) {
        validDate = false;
      }
      // Disable any dates after firstSelectableDate
      if (
        lastSelectableDate &&
        formattedInput.isAfter(formattedLastSelectableDate, "day")
      ) {
        validDate = false;
      }
      return !validDate;
    };
  }, [firstSelectableDate, lastSelectableDate]);

  const onCreate = useCallback(
    (
      flatpickrObject: Parameters<
        NonNullable<DateTimePickerProps["onCreate"]>
      >[0]
    ) => {
      if (
        flatpickrObject &&
        "clear" in flatpickrObject &&
        "close" in flatpickrObject &&
        "input" in flatpickrObject &&
        "calendarContainer" in flatpickrObject
      ) {
        flatpickrObjectRef.current = flatpickrObject;
        if (calendarContainerClass) {
          flatpickrObject.calendarContainer.className = cx(
            flatpickrObject.calendarContainer.className,
            calendarContainerClass
          );
        }
        // Ensure the alt input created by Flatpickr always has the desired id and classes
        const nextSibling = coalesce(flatpickrObject.input.nextSibling, null);
        if (nextSibling && "id" in nextSibling) {
          Object.assign(nextSibling, { id });
        }
      }
    },
    [calendarContainerClass]
  );

  const onChangeHandler = useCallback(
    (
      selectedDates: Date[] & Date & string[],
      dateStr: string,
      instance: FlatpickrController | null
    ) => {
      onChange(selectedDates);
      if (mode === "range" && selectedDates.length === 2) {
        instance?.close();
      }
    },
    [onChange, mode]
  );

  const disableDates = useMemo(() => {
    if (disabledDates) {
      return [disabledDates];
    }
    return [];
  }, [firstSelectableDate, lastSelectableDate]);

  const options: DateTimePickerProps["options"] = useMemo(
    () => ({
      allowInput,
      altFormat: dateFormat,
      altInput: true,
      altInputClass: inputClassNames,
      appendTo: attachTo || undefined,
      /**
       * Legacy code supports HTMLDivElement here even thought Flatpickr claims to only support HtmlElement.
       * HTMLDivElement is a subclass of HTMLElement with one additional property (align), so we can safely use it here.
       */
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/consistent-type-assertions */
      position: (position as any) || "auto",
      dateFormat: "Z",
      disableMobile: true,
      mode,
      wrap: true,
      defaultDate,
      disable: disableDates,
      plugins,
      closeOnSelect: mode === "range" ? false : true,
    }),
    [
      allowInput,
      dateFormat,
      inputClassNames,
      attachTo,
      position,
      mode,
      firstSelectableDate,
      lastSelectableDate,
    ]
  );

  let inputMaxWidthClass: string;
  switch (inputMaxWidth) {
    case "small":
      inputMaxWidthClass = styles.inputMaxWidthSmall;
      break;
    case "large":
      inputMaxWidthClass = styles.inputMaxWidthLarge;
      break;
    default:
      inputMaxWidthClass = styles.inputMaxWidthDefault;
  }

  return (
    <ControlWrapper
      tooltip={tooltip}
      wrapperClassNames={cx({
        [inputMaxWidthClass]: !fullWidth,
      })}
      label={label}
      helperText={helperText}
      optionalLabelText={optionalLabelText}
      disabled={disabled}
      error={error}
      inputId={id}
      fullWidth={fullWidth}
      renderControl={() => (
        <Flatpickr
          options={options}
          onOpen={onOpen}
          onCreate={onCreate}
          onClose={onClose}
          value={parsedValue}
          /**
           * Flatpickr requires onChange to be (date: Date[]) => void or date: Date) => void
           * However, before we converted this component to TS we had written a wrapper around the DatePicker component that forces onChange to be
           * (Date: string[]) => void. This apparently works with Flatpickr. Rather than updating the majority of our use cases to use the proper type
           * and potentially breaking things, we are going to ignore the type requirement here. That means if flatpickr ever updates their code to stop working with
           * (Date: string[]) => void, we will have breaking changes across the board.
           */
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore -
          onChange={onChangeHandler}
        >
          <div
            data-testid={testId}
            className={cx(styles.inputWrapper, {
              [styles["inputWrapper--error"]]: Boolean(error),
              [styles["inputWrapper--disabled"]]: disabled,
              [styles["inputWrapperCompact"]]: compact,
            })}
          >
            <input
              className={styles.inputElement}
              data-input
              defaultValue={
                defaultDate && typeof defaultDate === "string"
                  ? defaultDate
                  : undefined
              }
              data-testid="date-picker-input"
              placeholder={placeholder}
            />
            {showIcon && (
              <div className={styles.iconButton} data-toggle>
                <div className={styles.iconWrapper}>
                  <Icon
                    icon="calendar"
                    variant="thin"
                    size={24}
                    color={THEME_COLORS.textTertiary}
                  />
                </div>
              </div>
            )}
            {
              // istanbul ignore next - relies on the lifecycle hooks in Flatpickr, which doesn't play nicely with unit tests
              showClearButton && inputHasValue && (
                <button
                  className={styles.clearButton}
                  onClick={() => {
                    flatpickrObjectRef.current?.clear();
                  }}
                  data-testid="data-picker-clear-button"
                  title="Clear"
                  type="button"
                >
                  <div className={styles.iconWrapper}>
                    <Icon
                      icon="close"
                      variant="thin"
                      size={12}
                      color={THEME_COLORS.textTertiary}
                    />
                  </div>
                </button>
              )
            }
          </div>
        </Flatpickr>
      )}
    />
  );
}
